import { User } from 'firebase/auth'

import { generateApiPath } from '@taxfix/scouts-helpers'

import { CompanyReturnDTO } from '@/models'
import { CompanyDTO, CompanyOfficerDTO, UpdateCompanyPayload } from '@/models/company'

import { apiSlice } from '../api-slice'
import { BaseCompanyParams } from '../company-shared/models'
import { companyPrePaywallBaseQuery } from '../company-shared/query'
import { ENDPOINTS } from '../endpoints'
import { Tags } from '../tags'

export interface GetCompanyParams extends BaseCompanyParams {
    user: User | null
}

export interface CreateCompanyReturnParams extends BaseCompanyParams {
    user: User
}

export interface UpdateCompanyParams extends BaseCompanyParams, Partial<UpdateCompanyPayload> {}

export interface UpdateCompanyOfficersParams extends BaseCompanyParams, Pick<CompanyOfficerDTO, 'email'> {}

export interface UpdateCompanyOfficerParams extends BaseCompanyParams, Pick<CompanyOfficerDTO, 'email' | 'id'> {}

export interface UpdateCompanyOfficerIsPrimaryParams
    extends BaseCompanyParams,
        Pick<CompanyOfficerDTO, 'isPrimary' | 'id'> {}

export interface UpdateCompanyActingOnBehalfParams extends BaseCompanyParams {
    isUserActingOnBehalfOfDirectors: boolean
}

export interface GetCompanyOfficerParams extends BaseCompanyParams, Pick<CompanyOfficerDTO, 'id'> {}

interface UpdateCompanyNumberParams extends BaseCompanyParams {
    companyReturnId?: CompanyReturnDTO['id']
    companyNumber: string
    user: User | null // @TODO: This might not be needed
}

const extendedApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        createCompany: builder.mutation<CompanyDTO, { user: User | null }>({
            queryFn: async ({ user }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'POST',
                        url: generateApiPath(ENDPOINTS.COMPANIES.POST),
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyDTO }
            },
            invalidatesTags: [Tags.Companies],
        }),
        getCompanies: builder.query<CompanyDTO[], { user?: User | null }>({
            queryFn: async ({ user }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'GET',
                        url: generateApiPath(ENDPOINTS.COMPANIES.GET.LIST),
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyDTO[] }
            },
            providesTags: (result: CompanyDTO[] | undefined) =>
                result ? [...result.map(({ id }) => ({ type: Tags.Companies, id })), Tags.Companies] : [Tags.Companies],
        }),
        getCompany: builder.query<CompanyDTO, GetCompanyParams>({
            queryFn: async ({ companyId, user }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'GET',
                        url: generateApiPath(ENDPOINTS.COMPANIES.GET.SINGLE, { companyId }),
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyDTO }
            },
            providesTags: (_1, _2, arg) => [{ type: Tags.Companies, id: arg.companyId }],
        }),
        // This endpoint will be used when user is authenticated (post-paywall) hence the "query" (instead of "queryFn")
        updateCompany: builder.mutation<CompanyDTO, UpdateCompanyParams>({
            query: ({ companyId, ...payload }) => ({
                method: 'PUT',
                url: generateApiPath(ENDPOINTS.COMPANIES.PUT.UPDATE_COMPANY, {
                    companyId,
                }),
                data: payload,
            }),
            invalidatesTags: (_1, _2, arg) => [{ type: Tags.Companies, id: arg.companyId }],
        }),
        updateCompanyNumber: builder.mutation<CompanyDTO, UpdateCompanyNumberParams>({
            queryFn: async ({ companyId, companyNumber, user }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'PUT',
                        url: generateApiPath(ENDPOINTS.COMPANIES.PUT.COMPANY_NUMBER, { companyId, companyNumber }),
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyDTO }
            },
            invalidatesTags: (_1, _2, arg) => [
                { type: Tags.Companies, id: arg.companyId },
                ...(arg.companyReturnId ? [{ type: Tags.CompanyReturns, id: arg.companyReturnId }] : []),
            ],
        }),
        updateIsUserActingOnBehalfOfDirectors: builder.mutation<CompanyDTO, UpdateCompanyActingOnBehalfParams>({
            query: (params) => ({
                method: 'PUT',
                url: generateApiPath(ENDPOINTS.COMPANIES.PUT.ACTING_ON_BEHALF, params),
            }),
            invalidatesTags: (_1, _2, arg) => [{ type: Tags.Companies, id: arg.companyId }, Tags.CompanyOfficers],
        }),
    }),
})

export const {
    useCreateCompanyMutation,
    useGetCompaniesQuery,
    useLazyGetCompaniesQuery,
    useGetCompanyQuery,
    useUpdateCompanyMutation,
    useUpdateCompanyNumberMutation,
    useUpdateIsUserActingOnBehalfOfDirectorsMutation,
} = extendedApi
